import { useEffect } from "react";
import { Box, Text, Spinner, Flex, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";

import { Alert, Heading } from "@digital-services-gard-as/anchor-react";
import {
    getClaimRecipientDetailsQueryOptions,
    getClaimsInfoByIdQueryOptions
} from "@/api/queryOptions/claimsApiQueryOptions";
import { InvoiceFormData } from "@/types";
import { createInvoiceAddressText } from "@/utils/addressUtils";
import { sumNumberStrings } from "@/utils/invoiceUtils";

export const AddressInfo = ({ crmId }: { crmId?: string }) => {
    const { t } = useTranslation(["invoices", "errorMessages"]);

    const { watch, setValue } = useFormContext<InvoiceFormData>();
    const claimRef = watch("claimRef");
    const subClaimRef = watch("subClaimsRef");
    const totalVatAmountWatch = watch("totalVatAmount");
    const totalLocalTaxWatch = watch("totalLocalTax");

    const { data: claimRecipient, isLoading: isLoadingClaimRecipient } = useQuery(
        getClaimRecipientDetailsQueryOptions(claimRef, crmId)
    );

    const { data: claimsInfoById, isLoading: isLoadingClaimsInfo } = useQuery(getClaimsInfoByIdQueryOptions(claimRef));

    const selectedSubClaim = claimsInfoById?.subClaims.find(subClaim => subClaim.gardSubClaimRef === subClaimRef);
    // When switching claims, the subClaimRef from the old claim lingers momentarily,
    // so we need to check if the subClaimRef belongs to the currently selected claim
    const isValidSubClaimRef = Boolean(subClaimRef && selectedSubClaim);
    const claimsHandlerEmail =
        selectedSubClaim?.claimHandlerEmail || `${t("invoices:createInvoice.generalInfo.defaultClaimsHandlerEmail")}`;

    useEffect(() => {
        if (!claimRecipient) {
            return;
        }
        const clientVatNumber = claimRecipient.vatNumber || undefined;
        setValue("clientVat", clientVatNumber);
        if (!clientVatNumber) {
            const amount = sumNumberStrings(totalVatAmountWatch, totalLocalTaxWatch);
            setValue("totalLocalTax", amount.toString());
            setValue("totalVatAmount", "0");
        }
    }, [claimRecipient, setValue, totalLocalTaxWatch, totalVatAmountWatch]);

    const missingAddress = !claimRecipient?.address;
    if (!claimRef || (!isValidSubClaimRef && missingAddress)) {
        return null;
    }

    if (isLoadingClaimRecipient || isLoadingClaimsInfo) {
        return (
            <VStack>
                <Spinner />
                <Text>{t("loading")}</Text>
            </VStack>
        );
    }

    return (
        <Box>
            <Heading level="4" size="sm" id="invoiceTo">
                {t("invoices:createInvoice.generalInfo.invoiceTo")}
            </Heading>
            {missingAddress ? (
                <Alert variant="warning" data-testid="invoiceAddress">
                    <Flex flexDir="column" gap="2">
                        <Text>{t("invoices:createInvoice.generalInfo.missingAddress")}</Text>
                        <Text fontWeight="bold">{claimsHandlerEmail}</Text>
                    </Flex>
                </Alert>
            ) : (
                <Text whiteSpace="pre-wrap" overflowWrap="anywhere" lineHeight="tall" data-testid="invoiceAddress">
                    {createInvoiceAddressText(claimRecipient) || "—"}
                </Text>
            )}
        </Box>
    );
};
